import React from 'react';
import { Popover, Transition } from '@headlessui/react';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { parseJobDayClockTime } from '@core/util/jobScheduleBounds.util';

// Helper arrays for hour and minute options
const minuteOptions = [0, 15, 30, 45];

export type TimePickerIncrementType = 'quarter' | 'minute';

// Helper to combine date and time into a dayjs object
export const combineDateTime = (date: Date, time: string) => {
  const parts = parseJobDayClockTime(time);
  const hour = parts?.hour ?? 0;
  const minute = parts?.minute ?? 0;
  return dayjs(date).hour(hour).minute(minute).second(0).millisecond(0);
};

// Helper for 12/24 hour conversion (`HH:mm` or compact `HHmm`)
const to12Hour = (hhmm: string) => {
  if (!hhmm) return { hour: 12, minute: 0, ampm: 'am' };
  const parts = parseJobDayClockTime(hhmm);
  if (!parts) return { hour: 12, minute: 0, ampm: 'am' };
  let h = parts.hour % 12;
  if (h === 0) h = 12;
  const ampm = parts.hour >= 12 ? 'pm' : 'am';
  return { hour: h, minute: parts.minute, ampm };
};
const to24Hour = (hour: number, minute: number, ampm: string) => {
  let h = hour % 12;
  if (ampm === 'pm') h += 12;
  if (h === 24) h = 0;
  return `${h.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};
function format12Hour(val: string) {
  if (!val) return '';
  const parts = parseJobDayClockTime(val);
  if (!parts) return '';
  const hhmm = `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
  return dayjs(`2000-01-01T${hhmm}`).format('h:mm a');
}

export function parseTimeInput(raw: string): string | null {
  const cleaned = raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace('.', ':');
  if (!cleaned) return null;
  const meridiem = cleaned.match(/(am?|pm?)$/)?.[1];
  const digits = meridiem ? cleaned.slice(0, -meridiem.length) : cleaned;
  const match = digits.match(/^(\d{1,2}):?(\d{2})?$/);
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = match[2] === undefined ? 0 : Number(match[2]);
  if (minute > 59) return null;
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    hour = hour % 12;
    if (meridiem.startsWith('p')) hour += 12;
  } else if (hour > 23) {
    return null;
  }
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function TimePickerPopover({
  value,
  onChange,
  incrementType,
}: {
  value: string;
  onChange: (val: string) => void;
  incrementType: TimePickerIncrementType;
}) {
  const { hour, minute, ampm } = to12Hour(value || '09:00');
  const [h, setH] = React.useState(hour);
  const [m, setM] = React.useState(minute);
  const [ap, setAP] = React.useState(ampm);
  React.useEffect(() => {
    setH(hour);
    setM(minute);
    setAP(ampm);
  }, [value]);
  const setTime = (newH: number, newM: number, newAP: string) => {
    setH(newH);
    setM(newM);
    setAP(newAP);
    onChange(to24Hour(newH, newM, newAP));
  };
  const incHour = () => setTime(h === 12 ? 1 : h + 1, m, ap);
  const decHour = () => setTime(h === 1 ? 12 : h - 1, m, ap);
  const nextMinute = (currentMinute: number, direction: 1 | -1) => {
    if (incrementType === 'minute') {
      return (currentMinute + direction + 60) % 60;
    }
    // Quarter-hour stepping. If the current minute isn't on a quarter boundary
    // (e.g. after switching modes), snap to the nearest quarter in the chosen
    // direction rather than falling through to index -1.
    const currentIndex = minuteOptions.indexOf(currentMinute);
    if (currentIndex === -1) {
      const snappedIndex =
        direction === 1
          ? minuteOptions.findIndex((option) => option > currentMinute)
          : [...minuteOptions]
              .reverse()
              .findIndex((option) => option < currentMinute);
      if (direction === 1) {
        return minuteOptions[snappedIndex === -1 ? 0 : snappedIndex] as number;
      }
      const reversedIndex = snappedIndex === -1 ? 0 : snappedIndex;
      return minuteOptions[minuteOptions.length - 1 - reversedIndex] as number;
    }
    const nextIndex =
      (currentIndex + direction + minuteOptions.length) % minuteOptions.length;
    return minuteOptions[nextIndex] as number;
  };
  const incMinute = () => setTime(h, nextMinute(m, 1), ap);
  const decMinute = () => setTime(h, nextMinute(m, -1), ap);
  const toggleAP = () => setTime(h, m, ap === 'am' ? 'pm' : 'am');
  return (
    <div className="flex items-center justify-center gap-6 p-4">
      {/* Hour */}
      <div className="flex flex-col items-center">
        <button
          type="button"
          className="text-2xl text-gray-700 hover:text-purple-600"
          onClick={incHour}
        >
          &#9650;
        </button>
        <div className="my-1 text-2xl font-semibold">
          {h.toString().padStart(2, '0')}
        </div>
        <button
          type="button"
          className="text-2xl text-gray-700 hover:text-purple-600"
          onClick={decHour}
        >
          &#9660;
        </button>
      </div>
      <div className="my-1 text-2xl font-semibold">:</div>
      {/* Minute */}
      <div className="flex flex-col items-center">
        <button
          type="button"
          className="text-2xl text-gray-700 hover:text-purple-600"
          onClick={incMinute}
        >
          &#9650;
        </button>
        <div className="my-1 text-2xl font-semibold">
          {m.toString().padStart(2, '0')}
        </div>
        <button
          type="button"
          className="text-2xl text-gray-700 hover:text-purple-600"
          onClick={decMinute}
        >
          &#9660;
        </button>
      </div>
      {/* AM/PM */}
      <div className="ml-2 flex flex-col items-center">
        <button
          type="button"
          className="text-2xl text-gray-700 hover:text-purple-600"
          onClick={toggleAP}
        >
          &#9650;
        </button>
        <div className="my-1 text-2xl font-semibold uppercase">{ap}</div>
        <button
          type="button"
          className="text-2xl text-gray-700 hover:text-purple-600"
          onClick={toggleAP}
        >
          &#9660;
        </button>
      </div>
    </div>
  );
}

interface TimePickerProps {
  value: string;
  onChange: (val: string) => void;
  label?: string;
  defaultTime?: string;
  onBlur: (e: React.FocusEvent<any>) => void;
  isInvalid?: boolean;
  incrementType?: TimePickerIncrementType;
}

const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  label,
  defaultTime = null,
  onBlur,
  isInvalid = false,
  incrementType = 'quarter',
}) => {
  const displayValue = format12Hour(value || defaultTime || '');
  const [draft, setDraft] = React.useState<string | null>(null);
  const valueOnFocus = React.useRef(value);
  const cancelled = React.useRef(false);

  const commitDraft = (raw: string) => {
    const parsed = parseTimeInput(raw);
    if (parsed) onChange(parsed);
  };

  return (
    <div className="relative w-full min-w-0">
      {label && (
        <label className="mb-1 block text-sm font-medium">{label}</label>
      )}
      <Popover className="relative w-full" onBlur={onBlur}>
        {({ open, close }) => (
          <>
            <Popover.Button
              as="div"
              className={`flex w-full min-w-0 items-center justify-between rounded-sm border bg-white px-3 py-2 text-sm ${
                isInvalid
                  ? 'border-red-500 ring-1 ring-red-500'
                  : open
                    ? 'ring-2 ring-purple-500'
                    : 'focus-within:ring-2 focus-within:ring-purple-500'
              }`}
            >
              <input
                type="text"
                size={1}
                value={draft ?? displayValue}
                placeholder="--:--"
                aria-invalid={isInvalid || undefined}
                aria-label={label || 'Time'}
                className="block w-full min-w-0 bg-transparent text-left font-medium focus:outline-hidden"
                onChange={(event) => {
                  setDraft(event.target.value);
                  commitDraft(event.target.value);
                }}
                onFocus={(event) => {
                  valueOnFocus.current = value;
                  cancelled.current = false;
                  event.target.select();
                }}
                onBlur={(event) => {
                  if (!cancelled.current) commitDraft(event.target.value);
                  setDraft(null);
                }}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  if (open) event.stopPropagation();
                }}
                onKeyDown={(event) => {
                  event.stopPropagation();
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    event.currentTarget.blur();
                  }
                  if (event.key === 'Escape') {
                    cancelled.current = true;
                    if (valueOnFocus.current) onChange(valueOnFocus.current);
                    setDraft(null);
                    close();
                    event.currentTarget.blur();
                  }
                }}
              />
              <FontAwesomeIcon
                icon={faClock}
                className="ml-2 shrink-0 cursor-pointer text-gray-500"
                aria-hidden
              />
            </Popover.Button>
            <Transition
              as={React.Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel
                portal={false}
                anchor={{ to: 'bottom start', gap: '0.5rem' }}
                transition
                className="z-[100] w-max max-w-[calc(100vw-2rem)] min-w-[12rem] rounded-xl border border-gray-200 bg-white shadow-lg outline-hidden"
              >
                <TimePickerPopover
                  value={value || defaultTime || ''}
                  onChange={(val) => {
                    onChange(val);
                  }}
                  incrementType={incrementType}
                />
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};

export default TimePicker;
